import React, { useState, useMemo } from 'react';
import { Box, Typography, LinearProgress, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight, KeyboardArrowDown, KeyboardArrowRight, FirstPage, LastPage } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import OverflowTooltip from '../../../shared/OverflowTooltip';
import { 
  getPriorityColor, 
  getStatusColor, 
  getTaskTypeColor, 
  getAssigneeColor, 
  lightenColor 
} from '../calendar-view/calendarColorUtils';
import './GanttTaskList.scss';

const GanttTaskList = ({ 
  tasks = [], 
  onTaskClick, 
  isCollapsed, 
  onToggleCollapse,
  pagination = { currentPage: 1, totalPages: 1, totalCount: 0, pageSize: 10 },
  onFirstPage,
  onPrevPage,
  onNextPage,
  onLastPage,
  groupByOption = 'taskType',
  filters = {}
}) => {
  const { t } = useTranslation();
  const [expandedTasks, setExpandedTasks] = useState(new Set());
  const [expandedGroups, setExpandedGroups] = useState(new Set()); // Start with all groups collapsed

  // Use tasks from props for display, Redux store handles optimistic updates
  const currentTasks = tasks;

  const getAssigneeEntries = (task, assigneeFilterId) => {
    const assignees = Array.isArray(task.assignees) && task.assignees.length > 0
      ? task.assignees
      : (task.assignee ? [task.assignee] : []);
    if (assignees.length === 0) {
      if (assigneeFilterId) return [];
      return [{ key: 'Unassigned', label: 'Unassigned', assignee: null }];
    }
    const entries = assignees.map(a => {
      const name = a?.name || `${a?.first_name || ''} ${a?.last_name || ''}`.trim();
      const key = a?.id ?? name;
      return { key: key || 'Unassigned', label: name || 'Unassigned', assignee: a };
    });
    if (!assigneeFilterId) return entries;
    return entries.filter(entry => String(entry.assignee?.id || '') === String(assigneeFilterId));
  };

  const normalizeGroupValue = (value, fallback) => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string' || typeof value === 'number') return String(value).trim() || fallback;
    if (typeof value === 'object') {
      const label = value.name || value.label || value.title;
      if (label) return String(label).trim();
      if (value.id !== undefined && value.id !== null) return String(value.id);
      return fallback;
    }
    return String(value).trim() || fallback;
  };

  // Group tasks based on the selected groupByOption
  const groupedTasks = useMemo(() => {
    const groups = {};

    const assigneeFilterId = filters.assignee ? String(filters.assignee) : null;

    currentTasks.forEach(task => {
      let groupKey;
      let groupLabel;
      
      switch (groupByOption) {
        case 'taskType':
          groupKey = normalizeGroupValue(task.taskType, 'Other');
          groupLabel = `${groupKey}s`;
          break;
        case 'priority':
          groupKey = normalizeGroupValue(task.priority, 'No Priority');
          groupLabel = groupKey;
          break;
        case 'status': {
          const statusValue = task.status;
          const statusLabel = normalizeGroupValue(statusValue, 'No Status');
          groupKey = statusLabel || 'No Status';
          groupLabel = groupKey;
          break;
        }
        case 'assignee': {
          const entries = getAssigneeEntries(task, assigneeFilterId);
          if (entries.length === 0) return;
          entries.forEach(entry => {
            const instanceId = `${task.id}::${entry.key || entry.label}`;
            if (!groups[entry.label]) {
              groups[entry.label] = { tasks: [], label: entry.label };
            }
            groups[entry.label].tasks.push({
              ...task,
              __ganttInstanceId: instanceId,
              __ganttGroupKey: entry.key || entry.label
            });
          });
          return;
        }
        default:
          groupKey = task.taskType || 'Other';
          groupLabel = `${groupKey}s`;
      }
      
      if (!groups[groupKey]) {
        groups[groupKey] = {
          tasks: [],
          label: groupLabel
        };
      }
      groups[groupKey].tasks.push(task);
    });

    // Calculate date ranges for each group
    Object.keys(groups).forEach(groupKey => {
      const group = groups[groupKey];
      let earliestStart = null;
      let latestDue = null;
      
      group.tasks.forEach(task => {
        if (task.startDate) {
          const startDate = new Date(task.startDate);
          if (!earliestStart || startDate < earliestStart) {
            earliestStart = startDate;
          }
        }
        
        if (task.dueDate) {
          const dueDate = new Date(task.dueDate);
          if (!latestDue || dueDate > latestDue) {
            latestDue = dueDate;
          }
        }
      });
      
      // Format date range
      if (earliestStart && latestDue) {
        const formatDate = (date) => {
          return date.toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short'
          });
        };
        
        if (earliestStart.toDateString() === latestDue.toDateString()) {
          // Same date
          group.dateRange = formatDate(earliestStart);
        } else {
          // Date range
          group.dateRange = `${formatDate(earliestStart)} to ${formatDate(latestDue)}`;
        }
      } else if (earliestStart) {
        group.dateRange = `From ${earliestStart.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
      } else if (latestDue) {
        group.dateRange = `Until ${latestDue.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
      } else {
        group.dateRange = 'No dates';
      }
    });

    return groups;
  }, [currentTasks, groupByOption, filters.assignee]);

  // Get all available group keys, sorted
  const availableGroupKeys = useMemo(() => {
    return Object.keys(groupedTasks).sort();
  }, [groupedTasks]);

  // Flatten tasks with grouping and nesting (no client-side pagination)
  const flattenedTasks = useMemo(() => {
    const result = [];
    
    const addTaskWithChildren = (task, level = 0) => {
      result.push({ ...task, level, isTask: true });
      // Only expand one subtask level (group -> root task -> subtask)
      if (level === 0 && expandedTasks.has(task.id) && task.subtasks?.length > 0) {
        task.subtasks.forEach(subtask => {
          result.push({ ...subtask, level: level + 1, isTask: true });
        });
      }
    };

    // Add groups for all available group keys
    availableGroupKeys.forEach(groupKey => {
      const group = groupedTasks[groupKey];
      if (group.tasks.length > 0) {
        result.push({ 
          id: `group-${groupKey.toLowerCase().replace(/\s+/g, '-')}`, 
          title: group.label, 
          groupKey: groupKey,
          isGroup: true, 
          level: 0,
          count: group.tasks.length,
          dateRange: group.dateRange
        });
        
        if (expandedGroups.has(groupKey)) {
          group.tasks.forEach(task => addTaskWithChildren(task, 0));
        }
      }
    });

    return result;
  }, [groupedTasks, expandedTasks, expandedGroups, availableGroupKeys]);


  const toggleExpand = (taskId) => {
    setExpandedTasks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const toggleGroupExpand = (groupKey) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  const calculateProgress = (task) => {
    // Check multiple possible completion field names
    const isCompleted = task.completed || task.marked_as_completed || task.is_completed || false;
    
    if (!task.subtasks?.length) return isCompleted ? 100 : 0;
    
    // Only consider immediate subtasks (no nested subtasks inside subtasks)
    let totalProgress = 0;
    let totalWeight = 0;

    task.subtasks.forEach(subtask => {
      const subtaskCompleted = subtask.completed || subtask.marked_as_completed || subtask.is_completed || false;
      totalProgress += subtaskCompleted ? 100 : 0;
      totalWeight += 1;
    });

    return totalWeight > 0 ? totalProgress / totalWeight : 0;
  };

  const getTaskColor = (task) => {
    if (!groupByOption) return null;

    switch (groupByOption) {
      case 'priority':
        return getPriorityColor(task.priority);
      case 'status':
        return getStatusColor(task.status);
      case 'taskType':
        return getTaskTypeColor(task.taskType);
      case 'assignee':
        return getAssigneeColor(task.assignees?.[0] || task.assignee);
      default:
        return null;
    }
  };

  const getGroupColor = (groupKey) => {
    if (!groupByOption) return null;

    // Create a mock task object to get the color for this group
    let mockTask = {};
    switch (groupByOption) {
      case 'priority':
        mockTask.priority = groupKey === 'No Priority' ? null : groupKey;
        break;
      case 'status':
        mockTask.status = groupKey === 'No Status' ? null : groupKey;
        break;
      case 'taskType':
        mockTask.taskType = groupKey === 'Other' ? null : groupKey;
        break;
      case 'assignee':
        if (groupKey === 'Unassigned') {
          mockTask.assignee = null;
        } else {
          const nameParts = groupKey.split(' ');
          mockTask.assignee = {
            first_name: nameParts[0] || '',
            last_name: nameParts[1] || '',
            name: groupKey
          };
        }
        break;
      default:
        return null;
    }
    
    return getTaskColor(mockTask);
  };

  const renderGroupColorIndicator = (groupKey) => {
    const color = getGroupColor(groupKey);
    if (!color) return null;

    const lightColor = lightenColor(color, 85);
    
    return (
      <Box 
        className="gantt-group-color-indicator"
        style={{
          '--gantt-group-color': color,
          '--gantt-group-border': lightColor
        }}
      />
    );
  };

  const renderEmptyState = () => {
    const isEpicFilter = filters.epic && filters.epic !== 'all';
    
    return (
      <Box className="gantt-task-list-empty">
        <Typography variant="h6" className="gantt-empty-title">
          {isEpicFilter ? t('gantt.emptyStates.noEpicTasks') : t('gantt.emptyStates.noTasks')}
        </Typography>
        <Typography variant="body2" className="gantt-empty-suggestion">
          {t('gantt.emptyStates.adjustFilters')}
        </Typography>
      </Box>
    );
  };

  return (
    <Box className={`gantt-task-list ${isCollapsed ? 'collapsed' : ''}`}>
      <Box className="gantt-task-list-header">
        <Typography className="gantt-task-list-title">
          {t('gantt.taskList')}
        </Typography>
        <Typography className="gantt-task-list-collapsed-label">
          {t('gantt.taskList')}
        </Typography>
        <IconButton 
          className="gantt-task-list-toggle"
          onClick={onToggleCollapse}
          size="small"
        >
          <ChevronLeft />
        </IconButton>
      </Box>

      <Box className="gantt-task-list-content custom-scrollbar">
        {flattenedTasks.length === 0 ? (
          renderEmptyState()
        ) : (
          flattenedTasks.map((item) => {
            if (item.isGroup) {
              // Render group header
              return (
                <Box key={item.id} className="gantt-task-group-wrapper">
                  <Box 
                    className="gantt-task-group"
                    onClick={() => toggleGroupExpand(item.groupKey)}
                  >
                    <IconButton
                      className="gantt-task-group-expand-icon"
                      size="small"
                    >
                      {expandedGroups.has(item.groupKey) ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                    </IconButton>
                    {renderGroupColorIndicator(item.groupKey)}
                    <Box className="gantt-task-group-header">
                      <OverflowTooltip
                        title={`${item.title} (${item.count})`}
                        tooltipWidth={250}
                        variant="inherit"
                        className="gantt-task-group-title"
                        placement="top-start"
                      >
                        {item.title} ({item.count})
                      </OverflowTooltip>
                      <Typography className="gantt-task-group-date-range" variant="caption">
                        {item.dateRange}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            } else {
              // Render task item
              return (
                <Box key={item.__ganttInstanceId || item.id} className="gantt-task-item-wrapper">
                  <Box 
                    className="gantt-task-item"
                    style={{ '--gantt-indent': `${item.level * 16 + 12}px` }}
                    onClick={() => onTaskClick?.(item)}
                  >
                  <Box className="gantt-task-expand-placeholder">
                    {item.level === 0 && item.subtasks?.length > 0 && (
                      <IconButton
                        className="gantt-task-expand-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpand(item.id);
                        }}
                        size="small"
                      >
                        {expandedTasks.has(item.id) ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                      </IconButton>
                    )}
                  </Box>
                  <Box className="gantt-task-details">
                    <OverflowTooltip
                      title={`${item.ticketNumber} ${item.title}`}
                      tooltipWidth={300}
                      variant="inherit"
                      className="gantt-task-title"
                      placement="top-start"
                    >
                      {item.ticketNumber} {item.title}
                    </OverflowTooltip>
                    <LinearProgress 
                      variant="determinate" 
                      value={calculateProgress(item)} 
                      className="gantt-task-progress"
                    />
                  </Box>
                </Box>
                <Box className="gantt-task-separator" />
              </Box>
              );
            }
          })
        )}
      </Box>

      {(flattenedTasks.length > 0 && pagination.totalPages > 0) && (
        <Box className="gantt-task-list-pagination">
          <Box className="gantt-pagination-controls">
            <IconButton 
              onClick={onFirstPage} 
              disabled={pagination.currentPage === 1 || pagination.totalPages === 0}
              size="small"
            >
              <FirstPage />
            </IconButton>
            <IconButton 
              onClick={onPrevPage} 
              disabled={pagination.currentPage === 1 || pagination.totalPages === 0}
              size="small"
            >
              <ChevronLeft />
            </IconButton>
          </Box>
          
          <Box className="gantt-pagination-info">
            <Typography className="gantt-task-list-range-info" variant="inherit">
              {pagination.totalPages === 0 ? '' : t('gantt.showing') + ' ' + ((pagination.currentPage - 1) * pagination.pageSize + 1) + '–' + Math.min(pagination.currentPage * pagination.pageSize, pagination.totalCount) + ' ' + t('gantt.ofTasks', { total: pagination.totalCount })}
            </Typography>
          </Box>
          
          <Box className="gantt-pagination-controls">
            <IconButton 
              onClick={onNextPage} 
              disabled={pagination.currentPage === pagination.totalPages || pagination.totalPages === 0}
              size="small"
            >
              <ChevronRight />
            </IconButton>
            <IconButton 
              onClick={onLastPage} 
              disabled={pagination.currentPage === pagination.totalPages || pagination.totalPages === 0}
              size="small"
            >
              <LastPage />
            </IconButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default GanttTaskList;
